import { Component, ElementRef, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';

import { NavigationDestinationInterface, NavigationDomainInterface } from '../../../interfaces';
import { NavigationStateService, navigationKey } from '../../../services';

/** Domínios que formam o grupo "Administração"; os demais são "Operação". */
const ADMIN_DOMAIN_IDS = new Set(['team', 'billing', 'settings']);

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

@Component({
  selector: 'app-mobile-navigation-drawer',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, LucideAngularModule],
  templateUrl: './mobile-navigation-drawer.component.html',
  styleUrl: './mobile-navigation-drawer.component.scss',
  host: { '(document:keydown.escape)': 'close()' }
})
export class MobileNavigationDrawerComponent {
  private readonly nav = inject(NavigationStateService);
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');

  readonly open = input(false);
  readonly domains = input.required<NavigationDomainInterface[]>();
  readonly closed = output<void>();

  protected readonly expandedIds = signal<ReadonlySet<string>>(new Set());
  protected readonly operationDomains = computed(() =>
    this.domains().filter((domain) => !ADMIN_DOMAIN_IDS.has(domain.id))
  );
  protected readonly adminDomains = computed(() =>
    this.domains().filter((domain) => ADMIN_DOMAIN_IDS.has(domain.id))
  );

  constructor() {
    effect(() => {
      if (this.open()) {
        untracked(() => this.onOpened());
      } else {
        untracked(() => this.onClosed());
      }
    });
  }

  protected close(): void {
    if (this.open()) {
      this.closed.emit();
    }
  }

  protected toggle(domain: NavigationDomainInterface): void {
    this.expandedIds.update((current) => {
      const next = new Set(current);

      if (!next.delete(domain.id)) {
        next.add(domain.id);
      }

      return next;
    });
  }

  protected isExpanded(domain: NavigationDomainInterface): boolean {
    return this.expandedIds().has(domain.id);
  }

  protected isDomainActive(domain: NavigationDomainInterface): boolean {
    return this.nav.activeDomainId() === domain.id;
  }

  protected isDestinationActive(destination: NavigationDestinationInterface): boolean {
    return this.nav.activeRoute() === navigationKey(destination);
  }

  protected testId(destination: Pick<NavigationDestinationInterface, 'route' | 'queryParams'>): string {
    return navigationKey(destination).replace(/^\//, '').replace(/[/?=&]/g, '-');
  }

  protected trapFocus(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }

    const focusable = Array.from(this.panel()?.nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!first || !last) {
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Reabre com a localização atual preservada: o domínio ativo vem expandido. */
  private onOpened(): void {
    const activeId = this.nav.activeDomainId();
    const active = this.domains().find((domain) => domain.id === activeId && !domain.directRoute);

    this.expandedIds.set(new Set(active ? [active.id] : []));
    setTimeout(() => this.closeButton()?.nativeElement.focus());
  }

  private onClosed(): void {
    const menuButton = document.querySelector<HTMLElement>('[data-testid="mobile-bottom-bar-menu"]');

    // devolve o foco só se ele estava dentro do painel (ex.: fechou pelo botão ou ESC)
    if (this.panel()?.nativeElement.contains(document.activeElement)) {
      menuButton?.focus();
    }
  }
}
